/**
 * Utility functions for exporting data to CSV format
 */

/**
 * Convert an array of objects to CSV format
 * @param {Array} data - Array of objects to convert
 * @param {Array} columns - Array of column definitions [{key: 'field', label: 'Header'}]
 * @returns {string} CSV formatted string
 */
export const convertToCSV = (data, columns) => {
    if (!data || data.length === 0) {
        return '';
    }

    // Create header row
    const headers = columns.map(col => col.label || col.key);
    const headerRow = headers.join(',');

    // Create data rows
    const dataRows = data.map(row => {
        return columns.map(col => {
            let value = row[col.key];
            
            // Handle null/undefined
            if (value === null || value === undefined) {
                value = '';
            }
            
            // Format dates
            if (col.type === 'date' && value) {
                const date = new Date(value);
                value = date.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                });
            }
            
            // Format numbers
            if (col.type === 'number' && value !== '') {
                value = parseFloat(value).toFixed(2);
            }
            
            // Escape commas and quotes in string values
            if (typeof value === 'string') {
                // Replace double quotes with two double quotes (CSV escaping)
                value = value.replace(/"/g, '""');
                // Wrap in quotes if contains comma, newline, or quote
                if (value.includes(',') || value.includes('\n') || value.includes('"')) {
                    value = `"${value}"`;
                }
            }
            
            return value;
        }).join(',');
    });

    // Combine header and data rows
    return [headerRow, ...dataRows].join('\n');
};

/**
 * Download CSV file
 * @param {string} csvContent - CSV formatted string
 * @param {string} filename - Name of the file to download
 */
export const downloadCSV = (csvContent, filename = 'export.csv') => {
    // Add BOM for Excel compatibility with special characters
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up the URL object
    URL.revokeObjectURL(url);
};

/**
 * Export timesheet data to CSV
 * @param {Array} timesheetData - Array of timesheet entries
 * @param {string} projectName - Name of the project (for filename)
 */
export const exportTimesheetToCSV = (timesheetData, projectName = 'project') => {
    const columns = [
        { key: 'date', label: 'Date', type: 'date' },
        { key: 'user_name', label: 'User' },
        { key: 'user_email', label: 'Email' },
        { key: 'task_display_id', label: 'Task ID' },
        { key: 'task_title', label: 'Task Title' },
        { key: 'feature_tag_name', label: 'Feature Tag' },
        { key: 'hours', label: 'Hours', type: 'number' },
        { key: 'notes', label: 'Notes' },
        { key: 'status', label: 'Status' }
    ];

    const csvContent = convertToCSV(timesheetData, columns);
    
    // Generate filename with date range if available
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0];
    const sanitizedProjectName = projectName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    const filename = `${sanitizedProjectName}_timesheet_${dateStr}.csv`;
    
    downloadCSV(csvContent, filename);
};

/**
 * Export grouped timesheet data to CSV (grouped by task)
 * @param {Array} groupedData - Array of grouped timesheet entries
 * @param {string} projectName - Name of the project (for filename)
 */
export const exportGroupedTimesheetToCSV = (groupedData, projectName = 'project') => {
    const columns = [
        { key: 'task_display_id', label: 'Task ID' },
        { key: 'task_title', label: 'Task Title' },
        { key: 'feature_tag_name', label: 'Feature Tag' },
        { key: 'hours', label: 'Total Hours', type: 'number' },
        { key: 'users', label: 'Users' },
        { key: 'date_range', label: 'Date Range' },
        { key: 'entry_count', label: 'Entries' }
    ];

    const csvContent = convertToCSV(groupedData, columns);
    
    // Generate filename
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0];
    const sanitizedProjectName = projectName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    const filename = `${sanitizedProjectName}_timesheet_grouped_${dateStr}.csv`;
    
    downloadCSV(csvContent, filename);
};

